import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";

const SettingsContext = createContext(null);
export const useSettings = () => useContext(SettingsContext);

const DEFAULTS = {
  site_name: "Gulf Coast VIP",
  logo_url: "/brand/gcv-icon.png",
  favicon_url: "/brand/gcv-icon.png",
  primary_color: "#df1721",
  accent_color: "#a48484",
  custom_css: "",
  card_styles: { events: "default", venues: "default", artists: "default" },
  logo_icon_url: "/brand/gcv-icon.png",
  logo_horizontal_url: "/brand/gcv-logo-horizontal.png",
  logo_stacked_url: "/brand/gcv-logo-stacked.png",
  header_logo_variant: "icon",
  logo_width: 36,
  logo_height: 36,
  logo_mobile_width: 32,
  logo_mobile_height: 32,
  logo_mobile_icon_only: true,
  logo_align: "left",
  logo_padding: 0,
  hero_headline: "Where the coast comes alive",
  hero_tagline: "Discover live music, events, artists and venues across the Gulf Coast.",
  hero_image: "https://images.pexels.com/photos/3122799/pexels-photo-3122799.jpeg",
  sections: { featured_events: true, popular_cities: true, featured_venues: true, featured_artists: true, florida_alabama: true, cta: true, newsletter: true },
  footer_text: "Discover live music, events, artists and venues across Florida & Alabama.",
  social_links: {},
  header: {
    show_search: true,
    cta_label: "Join",
    cta_href: "/register",
    links: [
      { label: "Events", href: "/events" },
      { label: "Venues", href: "/venues" },
      { label: "Artists", href: "/artists" },
      { label: "Gigs", href: "/gigs" },
      { label: "Submit", href: "/submit" },
    ],
  },
  footer: {
    tagline: "Discover live music, events, artists and venues across the Gulf Coast.",
    copyright: "",
    columns: [
      { title: "Discover", links: [{ label: "Events", href: "/events" }, { label: "Venues", href: "/venues" }, { label: "Artists", href: "/artists" }] },
      { title: "Get Listed", links: [{ label: "Submit Venue", href: "/submit" }, { label: "Submit Event", href: "/submit" }, { label: "Submit Artist", href: "/submit" }] },
      { title: "Regions", links: [{ label: "Florida", href: "" }, { label: "Alabama", href: "" }, { label: "The Gulf Coast", href: "" }] },
    ],
  },
};

function darken(hex, amt = 0.12) {
  const n = parseInt(hex.replace("#", ""), 16);
  let r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  r = Math.max(0, Math.round(r * (1 - amt)));
  g = Math.max(0, Math.round(g * (1 - amt)));
  b = Math.max(0, Math.round(b * (1 - amt)));
  return `rgb(${r},${g},${b})`;
}
function rgba(hex, a) {
  const n = parseInt(hex.replace("#", ""), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
}

export function applyTheme(primary, accent) {
  const p = primary || DEFAULTS.primary_color;
  const a = accent || DEFAULTS.accent_color;
  const root = document.documentElement;
  root.style.setProperty("--primary", p);
  root.style.setProperty("--accent", a);
  const pHover = darken(p, 0.12);
  const css = `
.bg-rose-500{background-color:${p}!important}
.hover\\:bg-rose-600:hover{background-color:${pHover}!important}
.text-rose-300{color:${rgba(p, 0.85)}!important}
.text-rose-400{color:${p}!important}
.text-rose-500{color:${p}!important}
.border-rose-500{border-color:${p}!important}
.focus\\:border-rose-500:focus{border-color:${p}!important}
.ring-rose-500{--tw-ring-color:${p}!important}
.bg-rose-500\\/10{background-color:${rgba(p, 0.1)}!important}
.bg-rose-500\\/15{background-color:${rgba(p, 0.15)}!important}
.bg-rose-500\\/20{background-color:${rgba(p, 0.2)}!important}
.from-rose-500\\/15{--tw-gradient-from:${rgba(p, 0.15)} var(--tw-gradient-from-position)!important;--tw-gradient-to:${rgba(p, 0)} var(--tw-gradient-to-position)!important;--tw-gradient-stops:var(--tw-gradient-from),var(--tw-gradient-to)!important}
.to-orange-500\\/10{--tw-gradient-to:${rgba(a, 0.1)} var(--tw-gradient-to-position)!important}
`;
  let el = document.getElementById("gcl-theme");
  if (!el) { el = document.createElement("style"); el.id = "gcl-theme"; document.head.appendChild(el); }
  el.textContent = css;
}

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(DEFAULTS);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/settings");
      const merged = {
        ...DEFAULTS, ...data,
        sections: { ...DEFAULTS.sections, ...(data.sections || {}) },
        card_styles: { ...DEFAULTS.card_styles, ...(data.card_styles || {}) },
        social_links: data.social_links || {},
        header: { ...DEFAULTS.header, ...(data.header || {}) },
        footer: { ...DEFAULTS.footer, ...(data.footer || {}) },
      };
      setSettings(merged);
      applyTheme(merged.primary_color, merged.accent_color);
      document.title = merged.site_name;
    } catch { applyTheme(DEFAULTS.primary_color, DEFAULTS.accent_color); }
    setLoaded(true);
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let el = document.getElementById("gcl-custom-css");
    if (!el) { el = document.createElement("style"); el.id = "gcl-custom-css"; document.head.appendChild(el); }
    el.textContent = settings.custom_css || "";
  }, [settings.custom_css]);

  useEffect(() => {
    const href = settings.favicon_url || settings.logo_url;
    if (!href) return;
    let link = document.querySelector("link[rel='icon']");
    if (!link) { link = document.createElement("link"); link.rel = "icon"; document.head.appendChild(link); }
    link.href = href;
  }, [settings.favicon_url, settings.logo_url]);

  return (
    <SettingsContext.Provider value={{ settings, reload: load, loaded }}>
      {children}
    </SettingsContext.Provider>
  );
}
